// Olympe
import { ErrorFlow } from 'olympe';

// Internal
import { warn } from './helpers';
import { getFlattenWrappers } from './wrappers';
import { handleDropdownCellChange, handleChevronCellChange, getCellValue } from './cells';
import { getColumns } from './columns';
import { triggerReload } from './triggers';

/**
 * @typedef {Object} RGChange
 * @property {(!Object|!CloudObject)} object
 * @property {(string|!PropertyModel)} property
 * @property {*} oldValue
 * @property {*} newValue
 * @property {!CellChange} metadata
 */
export const RGChange = {};

/**
 * Process the changes event from the reactgrid, and forward it to a lambda
 * @param {!BrickContext} $ 
 * @param {!Array} changes 
 */
export function handleOnCellsChanged($, changes) {
    // Only process changes when a lambda is defined
    const processor = $.get('Process Changes');
    if(!processor) {
        warn('no change is processed, the `Process Changes` lambda is no defined');
        return;
    }

    // Adapt the changes to olympe
    let internalStateChanged = false;
    const flattenWrappers = getFlattenWrappers($);
    const _changes = changes
        // Transform the changes to work well with Olympe
        .map(change => {
            // Get the required data
            const wrapper = flattenWrappers[change.rowId].wrapper;
            const columns = getColumns($, wrapper._rgLevel);
            const column = columns.find(c => c.Rank === change.columnId);
            
            // Guard
            if(column === undefined) {
                warn(`no column found for level ${wrapper._rgLevel} and rank ${change.columnId}`);
                return null;
            }

            // Handle dropdowns states
            if(change.type === 'dropdown') {
                handleDropdownCellChange($, change);
                internalStateChanged = true;
            }

            // Handle chevrons states
            else if(change.type === 'chevron') {
                handleChevronCellChange($, change, wrapper);
                internalStateChanged = true;
            }

            // Create the RGChange object
            return {
                object: wrapper.__rgGetObject(column['Property Path']),
                property: wrapper.__rgGetProperty(column['Property Path']),
                oldValue: getCellValue(change.previousCell),
                newValue: getCellValue(change.newCell),
                metadata: change
            };
        })
        // Filter out null change
        .filter(change => change !== null)
        // Filter out the dropdown change that are only about the internal state
        .filter(change => change.metadata.type !== 'dropdown' || change.newValue !== change.oldValue)
        // Filter out the chevron change that are only about the internal state
        .filter(change => change.metadata.type !== 'chevron' || change.newValue !== change.oldValue)
        // Filter out changes with no value, only if there is change with value -> hack to fix paste from excel until a better solution
        .filter((change, _index, all) => change.newValue !== "" || all.every(c => c.newValue === ""));

    // Guard
    if(_changes.length === 0) {
        if(internalStateChanged) {
            triggerReload($);
        }
        return;
    }

    // Call the lambda
    const [iFlow, iChanges] = processor.getInputs();
    const [oFlow, oErrorFlow] = processor.getOutputs();
    const processor$ = $.runner(processor);
    processor$.set(iChanges, _changes);
    processor$.trigger(iFlow);

    // Wait for the end of it
    Promise.race([
        processor$.waitFor(oFlow), 
        processor$.waitFor(oErrorFlow)
    ])
        .then(value => {
            // In case of error, throw it again
            if(value instanceof ErrorFlow) {
                $.throw(value);
            }

            // Success
            else {
                triggerReload($);
            }
        })

        // Free the lambda
        .finally(() => processor$.destroy());
}
